// Named operations an interface may call through the POS broker. This catalog is a versioned
// public contract: an operation's input and output never change incompatibly under the same
// name; a breaking change ships as a new name and the old one stays until no approved
// interface uses it. The backend (MaxOperations) re-validates every input against its own copy
// of these limits and is the only place an operation runs.
import type { Capability } from './spec.ts'
import { CONTENT_TYPES } from './catalog-labels.ts'
import {
  GET_IDS, LIST_IDS, LIST_LIMIT, LIST_OFFSET, LIST_QUERY, RESOURCES, SORT_DIR,
  getOperationName, listOperationName, updateOperationName
} from './resources.ts'

export const OPERATIONS_VERSION = 4

export type OperationMode = 'read' | 'write'

export interface OperationField {
  type: 'integer' | 'number' | 'string' | 'boolean' | 'ids' | 'list' | 'object'
  required?: boolean
  min?: number
  max?: number
  /** ids and list only */
  maxItems?: number
  pattern?: string
  values?: (string | number)[]
  /**
   * values lists what the operation supports, but another value is ignored (the default
   * applies) instead of rejected. Sort columns are lenient because a datatable header click
   * writes whichever column key was clicked. Lint still warns about a literal outside values.
   */
  lenient?: boolean
  /** list only: shape of each item */
  item?: Record<string, OperationField>
  description: string
}

export interface OutputField {
  /** timestamp is Unix seconds, formatted with date or datetime */
  type: 'integer' | 'number' | 'string' | 'boolean' | 'timestamp' | 'object' | 'list'
  nullable?: boolean
  description: string
  values?: (string | number)[]
  /** object and list only */
  item?: Record<string, OutputField>
}

/**
 * The capability depends on one input value, e.g. which report is read. With list, the input is
 * a comma-separated list of flags (reservations.list include): the operation's own capability
 * is still needed, and each listed flag adds what it maps to; a flag left out needs nothing
 * more. A list of capabilities means any one of them.
 */
export interface CapabilityByInput {
  input: string
  capabilities: Record<string, Capability | Capability[]>
  list?: boolean
}

export interface OperationDef {
  /** With capabilityByInput (not list), only a placeholder: the capability follows the input */
  capability: Capability
  capabilityByInput?: CapabilityByInput
  mode: OperationMode
  /** Minimum user level on top of the capability level */
  level: number
  /** Output identifies people. Approval copy mentions personal data. */
  personal?: boolean
  description: string
  input: Record<string, OperationField>
  /** Row fields of read results ({ rows }) */
  output?: Record<string, OutputField>
  /** Fields of the change facts writes return for confirmation */
  changes?: string[]
  /**
   * Writes only. always: every call asks the user to confirm, whatever the tool's confirmAbove
   * (the broker treats it as 0)
   */
  confirm?: 'always'
}

/**
 * Calls per user per tool within windowSeconds, by stage, and committed writes per tool per hour
 * across users. MaxBroker::LIMITS and SPEC_COMMITS_PER_HOUR enforce the same numbers.
 */
export const RATE_LIMITS = {
  windowSeconds: 60,
  perUser: { read: 90, prepare: 30, commit: 10 },
  commitsPerToolPerHour: 300,
  /** Background reruns of on.load (refresh, changes saved elsewhere): one run's reads per user and tool per windowSeconds, apart from perUser.read */
  background: { windowSeconds: 30, runsPerUser: 1 }
} as const

/** What callOperation hands to then (as result) and into, the same for every operation. */
export const OPERATION_RESULTS = {
  read: 'Reads resolve to { rows: [...], count } with the output fields of the operation. count is the match total (for paging), not the page length. *.list rows stay compact for search and paging. *.get returns the POS detail document (same shape johku-mcp get_* uses) after secret redaction; extra POS fields may appear. Missing ids are in result.missing. into a list state stores rows; into a record state stores rows[0]; into any other state stores the whole result. then sees result.rows and result.count. export.xlsx resolves to { url }: POS opens the file; into a string state stores the url.',
  write: 'While the tool is tried out before approval, every write first shows the changes POS reads from the database and saves only when the user confirms. Once approved, writes save at once unless the operation sets confirmAbove and the call changes more items than that, or the catalog operation has confirm: always (then every call asks). POS reports how many were saved and which were skipped, so the tool needs no toast for that. Writes resolve to { changes: [{ id, label, from, to }], unchanged: count of items that already had the value, missing: [ids not found], ineligible: [ids the operation does not allow the change for, such as an EAN on a reservable product] }. Create operations also set result.id to the new record so the tool can navigate. Writes that read the record back after saving (see the operation description) also set result.rows. POS enforces each operation\'s own rules (see its description), so a tool cannot change items the POS screens would not let the user change. into stores that object; then sees it as result.changes etc.',
  error: 'Leave catch out for the standard handling: POS shows the error message and stays silent when the user cancels a confirmation. catch replaces that and runs on every failure, including cancel (error = { code: \'cancelled\', message }). Actions cannot be conditional, so a toast in catch would also show an error on cancel; use catch only for actions that are right in both cases, such as resetting state.',
  limits: `Each user may make ${RATE_LIMITS.perUser.read} reads, ${RATE_LIMITS.perUser.prepare} write previews and ${RATE_LIMITS.perUser.commit} saves per tool per minute, and a tool may save ${RATE_LIMITS.commitsPerToolPerHour} times an hour across users. Only calls that ran count; a refused call does not. Past a limit the call fails with "Too many requests": POS shows it once, the rest of that event's actions do not run (catch included), and the tool neither refreshes nor reruns on.load in the background for 30 s. Load what a view needs in one call (resource_ids, ids, a wider from/to) instead of a call per row, and do not reload after every keystroke or change when one call at the end does. Background reruns of on.load (refresh, and the reload after another tool or device saves) do not use the read limit: they have their own, one on.load per ${RATE_LIMITS.background.windowSeconds} s per user and tool, and POS drops an extra one quietly.`,
  input: 'Input values that evaluate to null are left out. An empty string on an optional field is left out the same way, so a daterange can bind from/to to empty state while a period preset is active. A required empty string (such as clearing an EAN) is still sent. POS checks the evaluated input against catalog.operations[...].input before calling and runs catch with code \'invalid_input\' on a mismatch, including text outside a field\'s values. A lenient field (sort) ignores an unknown value instead, so a datatable header click on any column is safe.'
} as const

const ids = (description: string, maxItems = 200): OperationField => ({ type: 'ids', required: true, maxItems, description })
const ISO_DATE: OperationField = { type: 'string', max: 10, pattern: '^[0-9]{4}-[0-9]{2}-[0-9]{2}$', description: 'ISO date' }
/** Dates as POS stores them (d.m.Y) or ISO; the backend converts ISO */
const RANGE_DATE = '^([0-9]{1,2}\\.[0-9]{1,2}\\.[0-9]{4}|[0-9]{4}-[0-9]{2}-[0-9]{2})$'

/** Report types reports.query runs, with the capability each needs (that of its report source) */
export const QUERY_REPORTS: Record<string, Capability> = {
  sales: 'read:sales',
  groupedsales: 'read:sales',
  participants: 'read:reservations'
}

export const METADATA_ENTITIES = [
  'orders', 'products', 'reservations', 'bills', 'customers', 'billingcustomers', 'users',
  'subscriptions', 'items', 'deliveries', 'resources', 'resourcepools', 'categories',
  'collections', 'groups', 'tags', 'discounts', 'forms', 'messages', 'shows', 'pricetiers',
  'pricing', 'files', 'deliverymethods', 'routes', 'stops', 'zones', 'content', 'vouchers',
  'paymentplans', 'poses', 'seatmaps', 'rateplans', 'notifications'
] as const

const META_NS: OperationField = { type: 'string', max: 32, pattern: '^[a-z][a-z0-9_]{0,31}$', description: 'Metadata namespace, e.g. kitchen' }
const META_KEY: OperationField = { type: 'string', max: 64, pattern: '^[a-z][a-z0-9_]{0,63}$', description: 'Metadata key, e.g. ready' }
const META_LIST_FIELDS: Record<string, OperationField> = {
  meta_ns: { ...META_NS, description: 'With meta_key and meta_eq, keep rows whose metadata has this namespace.key' },
  meta_key: { ...META_KEY, description: 'With meta_ns and meta_eq. Object or array values do not match' },
  meta_eq: { type: 'string', max: 200, description: 'Exact match as text (true/false, number canonical, or string). Use with meta_ns and meta_key' },
  meta_sort_ns: { ...META_NS, description: 'With meta_sort_key, sort by this namespace.key; missing and object/array values last' },
  meta_sort_key: { ...META_KEY, description: 'With meta_sort_ns. sort_dir applies' }
}

/** Namespaces of each row's metadata to return with a list; * is every namespace the tool may read */
export const WITH_METADATA: OperationField = {
  type: 'string',
  max: 300,
  pattern: '^(\\*|[a-z][a-z0-9_]{0,31}(,[a-z][a-z0-9_]{0,31}){0,7})$',
  description: 'Comma-separated metadata namespaces to return on each row as metadata, e.g. cleaning,kitchen. * returns every namespace the tool may read'
}

const WITH_METADATA_OUTPUT: OutputField = { type: 'object', description: 'With with_metadata: the asked namespaces of the row, e.g. { cleaning: { status: "ready" } }; {} when none' }

function listInput (filters: Record<string, OperationField> | undefined, sort?: string[], meta = true, rowMetadata = false): Record<string, OperationField> {
  const input: Record<string, OperationField> = {
    query: LIST_QUERY,
    ids: LIST_IDS,
    limit: LIST_LIMIT,
    offset: LIST_OFFSET,
    ...(filters ?? {}),
    ...(meta ? META_LIST_FIELDS : {}),
    ...(rowMetadata ? { with_metadata: WITH_METADATA } : {})
  }
  if (sort?.length) {
    input.sort = { type: 'string', values: sort, lenient: true, description: `Sort column: ${sort.join(', ')}; another column keeps the default order` }
    input.sort_dir = SORT_DIR
  } else if (meta) {
    input.sort_dir = SORT_DIR
  }
  return input
}

function generatedOperations (): Record<string, OperationDef> {
  const out: Record<string, OperationDef> = {}
  for (const [id, def] of Object.entries(RESOURCES)) {
    const shopLocal = id !== 'issues' && id !== 'campaigns'
    const rowMetadata = shopLocal && (METADATA_ENTITIES as readonly string[]).includes(id)
    out[listOperationName(id)] = {
      capability: def.capability,
      mode: 'read',
      level: def.level ?? 3,
      personal: def.personal,
      description: def.list.description,
      input: listInput(def.list.filters, def.list.sort, shopLocal, rowMetadata),
      output: rowMetadata ? { ...def.list.output, metadata: WITH_METADATA_OUTPUT } : def.list.output,
      ...(def.list.capabilityByInput ? { capabilityByInput: def.list.capabilityByInput } : {})
    }
    if (def.get) {
      out[getOperationName(id)] = {
        capability: def.capability,
        mode: 'read',
        level: def.level ?? 3,
        personal: def.personal,
        description: def.get.description,
        input: { ids: GET_IDS, ...(def.get.input ?? {}) },
        output: {
          ...def.get.output,
          metadata: { type: 'object', description: 'Namespaced metadata document; {} when none' }
        }
      }
    }
    if (!def.update || !def.writeCapability) continue
    out[updateOperationName(id)] = {
      capability: def.writeCapability,
      mode: 'write',
      level: def.level ?? 3,
      description: def.update.description,
      input: {
        changes: {
          type: 'list',
          required: true,
          maxItems: 200,
          description: 'One item per record',
          item: {
            id: { type: 'integer', required: true, min: 1, description: 'Record id' },
            ...def.update.fields
          }
        }
      },
      changes: Object.keys(def.update.fields)
    }
  }
  return out
}

const NAMED: Record<string, OperationDef> = {
  'products.search': {
    capability: 'read:products',
    mode: 'read',
    level: 3,
    description: 'Find products by name or EAN, or load them by id. Rows are ordered by name. Prefer products.list when you need paging; this name stays so existing tools keep working.',
    input: {
      query: { type: 'string', max: 100, description: 'Matches part of the name or an exact EAN; empty or left out lists all' },
      ids: { type: 'ids', maxItems: 200, description: 'Load these products' },
      archived: { type: 'boolean', description: 'true lists archived products instead of current ones' },
      stocked: { type: 'boolean', description: 'true lists only products with a stock count' },
      missing_ean: { type: 'boolean', description: 'true lists only products that can have an EAN code (ean_allowed) but have none, so saving codes and reloading brings the next ones' },
      limit: LIST_LIMIT,
      offset: LIST_OFFSET
    },
    output: RESOURCES.products!.list.output
  },
  'products.setVisibility': {
    capability: 'write:products',
    mode: 'write',
    level: 3,
    description: 'Show or hide products in the online shop. Tickets are sold through their event, so showing one is skipped and returned in ineligible; hiding is always allowed',
    input: { ids: ids('Product ids'), visible: { type: 'boolean', required: true, description: 'true shows, false hides' } },
    changes: ['visible']
  },
  'products.setArchived': {
    capability: 'write:products',
    mode: 'write',
    level: 3,
    description: 'Archive or restore products; archiving also removes them from sales channels and pricings',
    input: { ids: ids('Product ids'), archived: { type: 'boolean', required: true, description: 'true archives, false restores' } },
    changes: ['archived']
  },
  'products.setEan': {
    capability: 'write:products',
    mode: 'write',
    level: 3,
    description: 'Set EAN codes of sellable products. Products that cannot have one (ean_allowed false in products.search) are skipped and returned in ineligible; clearing a code is always allowed',
    input: {
      changes: {
        type: 'list',
        required: true,
        maxItems: 200,
        description: 'One item per product',
        item: {
          id: { type: 'integer', required: true, min: 1, description: 'Product id' },
          ean: { type: 'string', required: true, max: 32, pattern: '^[0-9A-Za-z-]{0,32}$', description: 'Empty string clears the code' }
        }
      }
    },
    changes: ['ean']
  },
  'stock.setCount': {
    capability: 'write:stock',
    mode: 'write',
    level: 3,
    description: 'Record counted stock; Johku books the difference to the stock ledger',
    input: {
      counts: {
        type: 'list',
        required: true,
        maxItems: 200,
        description: 'One item per counted stock resource (stock_resource_id from products.search)',
        item: {
          resource_id: { type: 'integer', required: true, min: 1, description: 'Stock resource id' },
          count: { type: 'integer', required: true, min: 0, max: 1000000, description: 'Counted quantity' }
        }
      }
    },
    changes: ['stock']
  },
  'issues.setStatus': {
    capability: 'write:issues',
    mode: 'write',
    level: 3,
    description: 'Mark issues resolved or open again',
    input: { ids: ids('Issue ids', 100), status: { type: 'integer', required: true, values: [0, 1], description: '0 open, 1 resolved' } },
    changes: ['status']
  },
  'orders.setWorkflow': {
    capability: 'write:orders',
    mode: 'write',
    level: 3,
    personal: true,
    description: 'Move an order on a workflow board: add and remove column tags, optionally set processed. Not a generic order PATCH.',
    input: {
      id: { type: 'integer', required: true, min: 1, description: 'Order id' },
      add_tags: { type: 'ids', maxItems: 6, description: 'Tag ids to add' },
      remove_tags: { type: 'ids', maxItems: 6, description: 'Tag ids to remove' },
      processed: { type: 'boolean', description: 'Set processed when the destination column has a processed status' }
    },
    changes: ['tags', 'processed']
  },
  'orders.setProcessed': {
    capability: 'write:orders',
    mode: 'write',
    level: 3,
    personal: true,
    description: 'Mark orders processed or unprocessed, the same as the POS order list Set… action.',
    input: {
      ids: ids('Order ids'),
      processed: { type: 'boolean', required: true, description: 'true marks processed, false unprocessed' }
    },
    changes: ['processed']
  },
  'orders.setArchived': {
    capability: 'write:orders',
    mode: 'write',
    level: 3,
    personal: true,
    description: 'Archive orders the same way as the POS order list. Already archived ids are unchanged.',
    input: {
      ids: ids('Order ids'),
      remove_reservations: { type: 'boolean', description: 'true also removes reservations on the orders' }
    },
    changes: ['archived']
  },
  'orders.delete': {
    capability: 'write:orders',
    mode: 'write',
    level: 3,
    personal: true,
    description: 'Delete unpaid orders, the same eligibility as the POS order list. Paid orders go in ineligible. Always confirm.',
    input: { ids: ids('Order ids') },
    changes: ['deleted']
  },
  'reservations.setCheckIn': {
    capability: 'write:reservations',
    mode: 'write',
    level: 3,
    personal: true,
    description: 'Check guests in or clear check-in, the same as the reservation screen header button.',
    input: {
      ids: ids('Reservation ids'),
      clear: { type: 'boolean', description: 'true clears check-in; default sets it to now' }
    },
    changes: ['checkin_ts']
  },
  'reservations.setCheckOut': {
    capability: 'write:reservations',
    mode: 'write',
    level: 3,
    personal: true,
    description: 'Check guests out or clear check-out. Check-out needs a check-in; missing check-in goes in ineligible.',
    input: {
      ids: ids('Reservation ids'),
      clear: { type: 'boolean', description: 'true clears check-out; default sets it to now' }
    },
    changes: ['checkout_ts']
  },
  'reservations.setResources': {
    capability: 'write:reservations',
    mode: 'write',
    level: 3,
    personal: true,
    confirm: 'always',
    description: 'Move one reservation onto other resources (a room, table, guide, vehicle…): add and remove resource ids, the same save as the resources of the reservation screen. The price stays as it is, and locks and access codes follow the new resources. A removed resource\'s seat count moves to the resource added in its place. Every added resource must be free for the whole reservation: when it is not, the call fails and names the bookings in the way. A move may use a resource\'s extra (overbooking) seats, which only POS users can book; the confirmation says when it does. One reservation per call, and the user always confirms, whatever confirmAbove says. The reservation must keep at least one bookable resource (opening hours alone do not count).',
    input: {
      id: { type: 'integer', required: true, min: 1, description: 'Reservation id' },
      add: { type: 'ids', maxItems: 10, description: 'Resource ids to add, e.g. from resources.list' },
      remove: { type: 'ids', maxItems: 10, description: 'Resource ids to take off the reservation (resources[].id from reservations.list with include resources)' }
    },
    changes: ['resources']
  },
  'customers.create': {
    capability: 'write:customers',
    mode: 'write',
    level: 7,
    personal: true,
    description: 'Create a customer with a name, the same as the POS customer list. Resolves with result.id.',
    input: { name: { type: 'string', required: true, max: 200, description: 'Name' } },
    changes: ['name']
  },
  'customers.delete': {
    capability: 'write:customers',
    mode: 'write',
    level: 7,
    personal: true,
    description: 'Delete customers, the same as the customer screen Delete button. Always confirm.',
    input: { ids: ids('Customer ids') },
    changes: ['deleted']
  },
  'discounts.create': {
    capability: 'write:discounts',
    mode: 'write',
    level: 3,
    description: 'Create a discount code (amount 0) the same as the POS discount list. Resolves with result.id.',
    input: { code: { type: 'string', required: true, max: 80, description: 'Code' } },
    changes: ['code']
  },
  'discounts.delete': {
    capability: 'write:discounts',
    mode: 'write',
    level: 3,
    description: 'Delete discount codes. Always confirm.',
    input: { ids: ids('Discount ids') },
    changes: ['deleted']
  },
  'tags.create': {
    capability: 'write:tags',
    mode: 'write',
    level: 3,
    description: 'Create a tag. Stored as category:::title, or category:::locale:::title. Resolves with result.id.',
    input: {
      title: { type: 'string', required: true, max: 80, description: 'Title' },
      category: { type: 'string', required: true, values: ['product', 'order', 'admin', 'customer', 'resource'], description: 'Tag family' },
      locale: { type: 'string', max: 5, description: 'Language, e.g. fi_FI; omit for all versions' }
    },
    changes: ['title']
  },
  'tags.delete': {
    capability: 'write:tags',
    mode: 'write',
    level: 3,
    description: 'Delete tags. Always confirm.',
    input: { ids: ids('Tag ids') },
    changes: ['deleted']
  },
  'resources.create': {
    capability: 'write:resources',
    mode: 'write',
    level: 3,
    description: 'Create a resource or resource pool, the same as the POS resource list. Resolves with result.id.',
    input: {
      name: { type: 'string', required: true, max: 200, description: 'Name' },
      displayedtype: { type: 'string', required: true, values: ['buyable', 'reservable', 'openinghours', 'generic', 'resourcepool'], description: 'POS type; resourcepool creates a pool' }
    },
    changes: ['name']
  },
  'resources.setAvailability': {
    capability: 'write:resources',
    mode: 'write',
    level: 3,
    description: 'Add or remove availability ranges of one resource, the same ranges as the POS resource availability editor (ownRange). Every other range, setting and smart-lock link is kept. Stock (buyable) resources have no ranges and go in ineligible; ranges synced from a calendar cannot be removed. Resolves with result.rows: the ranges stored after the save.',
    input: {
      id: { type: 'integer', required: true, min: 1, description: 'Resource id' },
      add: {
        type: 'list',
        maxItems: 50,
        description: 'Ranges to add',
        item: {
          mode: { type: 'string', required: true, values: ['allow', 'allowstart', 'denyswitch', 'deny'], description: 'allow opens, allowstart allows starts only, denyswitch denies changeovers, deny closes' },
          datestart: { type: 'string', required: true, max: 10, pattern: RANGE_DATE, description: 'First day, d.m.Y or ISO' },
          dateend: { type: 'string', max: 10, pattern: RANGE_DATE, description: 'Last day, d.m.Y or ISO; default datestart, so one date is one day' },
          weekdays: { type: 'string', max: 13, pattern: '^[1-7](,[1-7]){0,6}$', description: 'Comma-separated weekdays, 1 = Monday; default every day' },
          timestart: { type: 'integer', min: 0, max: 1440, description: 'Minutes from midnight (600 = 10:00); default 0' },
          timeend: { type: 'integer', min: 0, max: 1440, description: 'Minutes from midnight; default 1440, the end of the day' }
        }
      },
      remove: { type: 'ids', maxItems: 100, description: 'Range ids to remove (ownRange[].id from resources.get)' }
    },
    changes: ['range']
  },
  'content.create': {
    capability: 'write:content',
    mode: 'write',
    level: 3,
    description: 'Create a leftover CMS page with a name and type, the same as the CMS content list. Shops without a CMS store cannot create pages. Resolves with result.id.',
    input: {
      name: { type: 'string', required: true, max: 200, description: 'Name' },
      type: { type: 'string', required: true, values: [...CONTENT_TYPES], description: 'CMS page type' }
    },
    changes: ['name']
  },
  'messages.update': {
    capability: 'write:messages',
    mode: 'write',
    level: 3,
    description: 'Change a shop message subject or body. type is the catalog key POS uses, not a numeric id.',
    input: {
      changes: {
        type: 'list',
        required: true,
        maxItems: 50,
        description: 'One item per message type',
        item: {
          type: { type: 'string', required: true, max: 80, description: 'Message type key' },
          subject: { type: 'string', max: 200, description: 'Subject' },
          text: { type: 'string', max: 20000, description: 'Body' }
        }
      }
    },
    changes: ['subject', 'text']
  },
  'export.xlsx': {
    capability: 'read:products',
    mode: 'read',
    level: 3,
    description: 'Build an Excel file of the current list. resource picks the list; pass the same filters or ids. Resolves to { url }; POS opens the download. Capability follows the resource: personal lists need a personal-data approval.',
    input: {
      resource: {
        type: 'string',
        required: true,
        values: ['orders', 'reservations', 'bills', 'customers', 'products', 'resources', 'discounts', 'content', 'tags', 'categories', 'issues'],
        description: 'Which list to export'
      },
      query: LIST_QUERY,
      ids: LIST_IDS,
      type: { type: 'string', max: 40, description: 'Same type filter as the list, when it has one' },
      tag_id: { type: 'integer', min: 1, description: 'orders.list tag_id' },
      pos: { type: 'string', max: 32, description: 'orders/bills pos' },
      from: { type: 'string', max: 10, pattern: '^[0-9]{4}-[0-9]{2}-[0-9]{2}$', description: 'ISO from' },
      to: { type: 'string', max: 10, pattern: '^[0-9]{4}-[0-9]{2}-[0-9]{2}$', description: 'ISO to' },
      period: { type: 'string', max: 40, description: 'List period preset' }
    },
    output: { url: { type: 'string', description: 'Shop-relative download path; POS appends the session key' } }
  },
  'reports.query': {
    capability: 'read:sales',
    capabilityByInput: { input: 'report_type', capabilities: QUERY_REPORTS },
    mode: 'read',
    level: 3,
    description: `Run a report for a date window of at most 366 days and read its output: the same document a report spec source of that type gets (see sourceNotes). The capability follows report_type: ${Object.entries(QUERY_REPORTS).map(([type, cap]) => `${type} needs ${cap}`).join(', ')}. rows[0].report is the output; long lists are cut short as in a report preview, with truncated set.`,
    input: {
      report_type: { type: 'string', required: true, values: Object.keys(QUERY_REPORTS), description: 'Report to run' },
      from: { ...ISO_DATE, required: true, description: 'First day (ISO)' },
      to: { ...ISO_DATE, required: true, description: 'Last day (ISO), included' },
      products: { type: 'ids', maxItems: 200, description: 'Limit to these products' },
      categories: { type: 'ids', maxItems: 200, description: 'Limit to products in these categories' }
    },
    output: {
      id: { type: 'string', description: 'The report type' },
      from: { type: 'string', description: 'First day (ISO)' },
      to: { type: 'string', description: 'Last day (ISO)' },
      report: { type: 'object', description: 'The report output, in the same shape as the report source of that type' },
      truncated: { type: 'boolean', description: 'true when long lists were cut short; each cut list has a <list>_truncated count of the rows left out' }
    }
  },
  'agenda.list': {
    capability: 'read:reservations',
    mode: 'read',
    level: 3,
    personal: true,
    description: 'Reservations grouped as the POS Agenda: bucket starting, ending or ongoing. Pass period or from/to; default thisweek.',
    input: {
      from: { type: 'string', max: 10, pattern: '^[0-9]{4}-[0-9]{2}-[0-9]{2}$', description: 'Window start (ISO); with to, overrides period' },
      to: { type: 'string', max: 10, pattern: '^[0-9]{4}-[0-9]{2}-[0-9]{2}$', description: 'Window end (ISO); with from, overrides period' },
      period: { type: 'string', max: 40, description: 'POS date preset when from/to are left out; default thisweek' },
      product_ids: { type: 'ids', maxItems: 10, description: 'Limit to these products' },
      limit: LIST_LIMIT,
      offset: LIST_OFFSET
    },
    output: {
      id: { type: 'integer', description: 'Reservation id' },
      bucket: { type: 'string', description: 'starting, ending or ongoing' },
      start_ts: { type: 'timestamp', description: 'Start' },
      end_ts: { type: 'timestamp', description: 'End' },
      product_name: { type: 'string', description: 'Product' },
      client_name: { type: 'string', nullable: true, description: 'Customer name' }
    }
  },
  'schedule.products': {
    capability: 'read:reservations',
    mode: 'read',
    level: 3,
    personal: true,
    description: 'Reservation and availability blocks for a product schedule (calendar kind product_schedule). At most 10 products. Pass period or from/to; default thisweek.',
    input: {
      product_ids: { type: 'ids', required: true, maxItems: 10, description: 'Product ids' },
      from: { type: 'string', max: 10, pattern: '^[0-9]{4}-[0-9]{2}-[0-9]{2}$', description: 'Window start (ISO); with to, overrides period' },
      to: { type: 'string', max: 10, pattern: '^[0-9]{4}-[0-9]{2}-[0-9]{2}$', description: 'Window end (ISO); with from, overrides period' },
      period: { type: 'string', max: 40, description: 'POS date preset when from/to are left out; default thisweek' }
    },
    output: {
      id: { type: 'integer', description: 'Reservation id when the block is a booking' },
      section: { type: 'string', description: 'Product section key, e.g. product-12' },
      start_ts: { type: 'timestamp', description: 'Start' },
      end_ts: { type: 'timestamp', description: 'End' },
      reservation_id: { type: 'integer', nullable: true, description: 'Reservation id' },
      name: { type: 'string', nullable: true, description: 'Customer or block label' },
      kind: { type: 'string', description: 'reservation or availability' }
    }
  },
  'schedule.resources': {
    capability: 'read:reservations',
    mode: 'read',
    level: 3,
    personal: true,
    description: 'Reservation and availability blocks for a resource schedule (calendar kind resource_schedule). At most 10 resources or pools. Pass period or from/to; default thisweek.',
    input: {
      resource_ids: { type: 'ids', maxItems: 10, description: 'Resource ids' },
      resource_pool_ids: { type: 'ids', maxItems: 10, description: 'Resource pool ids' },
      from: { type: 'string', max: 10, pattern: '^[0-9]{4}-[0-9]{2}-[0-9]{2}$', description: 'Window start (ISO); with to, overrides period' },
      to: { type: 'string', max: 10, pattern: '^[0-9]{4}-[0-9]{2}-[0-9]{2}$', description: 'Window end (ISO); with from, overrides period' },
      period: { type: 'string', max: 40, description: 'POS date preset when from/to are left out; default thisweek' }
    },
    output: {
      id: { type: 'integer', description: 'Reservation id when the block is a booking' },
      section: { type: 'string', description: 'Resource or pool section key' },
      start_ts: { type: 'timestamp', description: 'Start' },
      end_ts: { type: 'timestamp', description: 'End' },
      reservation_id: { type: 'integer', nullable: true, description: 'Reservation id' },
      name: { type: 'string', nullable: true, description: 'Customer or block label' },
      kind: { type: 'string', description: 'reservation or availability' }
    }
  },
  'settings.get': {
    capability: 'read:settings',
    mode: 'read',
    level: 3,
    description: 'Shop settings (GET settings/1.json) after the same secret redaction as johku-mcp get_settings. Payment titles and flags stay; API keys, tokens and passwords are [redacted]. Extra POS fields may appear.',
    input: {},
    output: {
      id: { type: 'integer', description: 'Always 1' },
      companyname: { type: 'string', nullable: true, description: 'Shop / company name' },
      shoptitle: { type: 'string', nullable: true, description: 'Shop title when the POS document has one' },
      senderemail: { type: 'string', nullable: true, description: 'Shop sender email' },
      shopemail: { type: 'string', nullable: true, description: 'Shop email' },
      shopphone: { type: 'string', nullable: true, description: 'Shop phone' },
      defaultversion: { type: 'string', nullable: true, description: 'Default content language' }
    }
  },
  'messages.get': {
    capability: 'read:messages',
    mode: 'read',
    level: 3,
    description: 'One shop message (email template or checkout text) by type, including subject and body.',
    input: {
      type: { type: 'string', required: true, max: 80, description: 'Message type key from messages.list' }
    },
    output: {
      id: { type: 'integer', description: 'Message id' },
      type: { type: 'string', description: 'Catalog key' },
      name: { type: 'string', description: 'POS title' },
      subject: { type: 'string', nullable: true, description: 'Subject' },
      text: { type: 'string', nullable: true, description: 'Body' }
    }
  },
  'resourcepools.get': {
    capability: 'read:resources',
    mode: 'read',
    level: 3,
    description: 'POS resource-pool documents. Extra POS fields may appear.',
    input: { ids: GET_IDS },
    output: {
      id: { type: 'integer', description: 'Pool id' },
      name: { type: 'string', description: 'Name' },
      sharedResource: { type: 'list', description: 'Member resources in pool priority order', item: { id: { type: 'integer', description: 'Resource id' }, name: { type: 'string', description: 'Name' }, displayedtype: { type: 'string', description: 'Resource type, as in resources.list' }, count: { type: 'integer', nullable: true, description: 'Capacity' } } },
      sharedResourcepool: { type: 'list', description: 'Child pools of a top-level pool', item: { id: { type: 'integer', description: 'Pool id' }, name: { type: 'string', description: 'Name' } } },
      metadata: { type: 'object', description: 'Namespaced metadata document; {} when none' }
    }
  },
  'products.availabledates': {
    capability: 'read:products',
    mode: 'read',
    level: 3,
    description: 'Days a product is reservable in from–to (at most 366 days, 10 products). Same as johku-mcp GET products/{id}/availabledates.json.',
    input: {
      product_ids: { type: 'ids', required: true, maxItems: 10, description: 'Product ids' },
      from: { type: 'string', required: true, max: 10, pattern: '^[0-9]{4}-[0-9]{2}-[0-9]{2}$', description: 'Window start (ISO)' },
      to: { type: 'string', required: true, max: 10, pattern: '^[0-9]{4}-[0-9]{2}-[0-9]{2}$', description: 'Window end (ISO)' }
    },
    output: {
      id: { type: 'string', description: 'product_id:ISO date' },
      product_id: { type: 'integer', description: 'Product id' },
      date: { type: 'string', description: 'ISO date' },
      available: { type: 'boolean', description: 'Bookable that day' }
    }
  },
  'products.availabletimes': {
    capability: 'read:products',
    mode: 'read',
    level: 3,
    description: 'Start times a product is reservable in from–to (at most 366 days, 10 products). Same as johku-mcp GET products/{id}/availabletimes.json.',
    input: {
      product_ids: { type: 'ids', required: true, maxItems: 10, description: 'Product ids' },
      from: { type: 'string', required: true, max: 10, pattern: '^[0-9]{4}-[0-9]{2}-[0-9]{2}$', description: 'Window start (ISO)' },
      to: { type: 'string', required: true, max: 10, pattern: '^[0-9]{4}-[0-9]{2}-[0-9]{2}$', description: 'Window end (ISO)' }
    },
    output: {
      id: { type: 'string', description: 'product_id:timestamp or time key' },
      product_id: { type: 'integer', description: 'Product id' },
      start_ts: { type: 'timestamp', nullable: true, description: 'Start' },
      available: { type: 'boolean', description: 'Bookable' }
    }
  },
  'availability.list': {
    capability: 'read:products',
    mode: 'read',
    level: 3,
    description: 'Shop-wide reservable products available in from–to. Same as johku-mcp GET availability.',
    input: {
      from: { type: 'string', required: true, max: 10, pattern: '^[0-9]{4}-[0-9]{2}-[0-9]{2}$', description: 'Window start (ISO)' },
      to: { type: 'string', required: true, max: 10, pattern: '^[0-9]{4}-[0-9]{2}-[0-9]{2}$', description: 'Window end (ISO)' }
    },
    output: {
      id: { type: 'integer', description: 'Product id' },
      name: { type: 'string', nullable: true, description: 'Product name' },
      available: { type: 'boolean', nullable: true, description: 'Available in the window' }
    }
  },
  'products.rateplans': {
    capability: 'read:products',
    mode: 'read',
    level: 3,
    description: 'Calendar rate plans of one product. Same as johku-mcp list_rateplans.',
    input: {
      product_id: { type: 'integer', required: true, min: 1, description: 'Product id' }
    },
    output: {
      id: { type: 'integer', description: 'Rate plan id' },
      product_id: { type: 'integer', description: 'Product id' },
      title: { type: 'string', nullable: true, description: 'Title' },
      is_default: { type: 'boolean', nullable: true, description: 'Default plan' }
    }
  },
  'products.restrictions': {
    capability: 'read:products',
    mode: 'read',
    level: 3,
    description: 'Restrictions of one rate plan. Same as johku-mcp GET products/{id}/rateplans/{id}/restrictions.json.',
    input: {
      product_id: { type: 'integer', required: true, min: 1, description: 'Product id' },
      rateplan_id: { type: 'integer', required: true, min: 1, description: 'Rate plan id' }
    },
    output: {
      id: { type: 'integer', description: 'Restriction id' },
      rateplan_id: { type: 'integer', nullable: true, description: 'Rate plan id' }
    }
  },
  'seatmaps.get': {
    capability: 'read:shows',
    mode: 'read',
    level: 3,
    description: 'Seat maps by id. Same as johku-mcp get_seatmap.',
    input: { ids: GET_IDS },
    output: {
      id: { type: 'integer', description: 'Seat map id' },
      name: { type: 'string', nullable: true, description: 'Name' },
      metadata: { type: 'object', description: 'Namespaced metadata document; {} when none' }
    }
  },
  'showschedule.get': {
    capability: 'read:shows',
    mode: 'read',
    level: 3,
    description: 'Event show schedule in a date window. Same as johku-mcp GET showschedule.json.',
    input: {
      product_id: { type: 'integer', min: 1, description: 'Limit to one event product' },
      from: { type: 'string', max: 10, pattern: '^[0-9]{4}-[0-9]{2}-[0-9]{2}$', description: 'Window start (ISO)' },
      to: { type: 'string', max: 10, pattern: '^[0-9]{4}-[0-9]{2}-[0-9]{2}$', description: 'Window end (ISO)' }
    },
    output: {
      id: { type: 'integer', description: 'Show id' },
      product_id: { type: 'integer', nullable: true, description: 'Product id' },
      start_ts: { type: 'timestamp', nullable: true, description: 'Start' },
      end_ts: { type: 'timestamp', nullable: true, description: 'End' }
    }
  },
  'metadata.get': {
    capability: 'read:metadata',
    mode: 'read',
    level: 3,
    personal: true,
    description: 'Read the namespaced metadata document of shop records, at most 100 at a time. To show metadata next to list rows, prefer with_metadata on the list. A tool reads the namespaces it declares in metadataNamespaces, or every namespace with metadataRead all. Missing ids are in result.missing. Each namespace written by metadata.set also has _by ({ id, name }: the user who saved it, the name as it was then) and _ts (unix time of the save), set by the server and returned with any of its keys, e.g. row.metadata.housekeeping._by.name; they cannot be written and do not count toward the key or size limits.',
    input: {
      entity: { type: 'string', required: true, values: [...METADATA_ENTITIES], description: 'Catalog resource, e.g. orders or products' },
      ids: { ...GET_IDS, maxItems: 100, description: 'Record ids, at most 100' },
      ns: { ...META_NS, description: 'Return only this namespace' },
      keys: { type: 'string', max: 400, pattern: '^[a-z][a-z0-9_]{0,63}(,[a-z][a-z0-9_]{0,63})*$', description: 'Comma-separated keys within ns; omit for every key' }
    },
    output: {
      id: { type: 'integer', description: 'Record id' },
      metadata: { type: 'object', description: 'Namespaced map, e.g. { kitchen: { ready: true, _by: { id: 4, name: "Anna" }, _ts: 1793160943 } }' }
    }
  },
  'metadata.set': {
    capability: 'write:metadata',
    mode: 'write',
    level: 3,
    personal: true,
    description: 'Set or delete namespaced metadata keys; null deletes a key. Only the namespaces in metadataNamespaces can be written. Does not require write access to the parent record. Last write wins. The server stamps each namespace the call changes with _by and _ts (see metadata.get); keys starting with _ are refused.',
    input: {
      entity: { type: 'string', required: true, values: [...METADATA_ENTITIES], description: 'Catalog resource, e.g. orders or products' },
      changes: {
        type: 'list',
        required: true,
        maxItems: 20,
        description: 'One item per record; at most 20 keys in the whole call',
        item: {
          id: { type: 'integer', required: true, min: 1, description: 'Record id' },
          ns: { ...META_NS, required: true, description: 'Namespace to write' },
          values: { type: 'object', required: true, description: 'Keys to set; null deletes the key' }
        }
      }
    },
    changes: ['metadata']
  }
}

export const OPERATIONS: Record<string, OperationDef> = { ...generatedOperations(), ...NAMED }

/** export.xlsx capability follows the listed resource, not the catalog placeholder. */
export const EXPORT_RESOURCES = ['orders', 'reservations', 'bills', 'customers', 'products', 'resources', 'discounts', 'content', 'tags', 'categories', 'issues'] as const
export type ExportResource = typeof EXPORT_RESOURCES[number]

export function exportCapability (resource: string): Capability | null {
  const def = RESOURCES[resource]
  return def && (EXPORT_RESOURCES as readonly string[]).includes(resource) ? def.capability : null
}

export function exportPersonal (resource: string): boolean {
  return !!RESOURCES[resource]?.personal
}

/**
 * Capabilities a call may need: the operation's own, or with capabilityByInput the one a
 * literal input value picks, and every listed one when the value is computed.
 *
 * A list input (include) needs the operation's own capability plus, for each literal flag, the
 * first of its capabilities the tool declares (else the first one, which lint then reports as
 * missing). A computed list needs, for each mapped flag, a capability only when the tool
 * declares one of its options; otherwise the broker refuses that flag when the call sends it.
 */
export function operationCapabilities (def: OperationDef, input: unknown, declared: readonly string[] = []): Capability[] {
  const by = def.capabilityByInput
  if (!by) return [def.capability]
  const value = input && typeof input === 'object' ? (input as Record<string, unknown>)[by.input] : undefined
  const options = (flag: string): Capability[] => {
    const caps = by.capabilities[flag]
    return caps === undefined ? [] : Array.isArray(caps) ? caps : [caps]
  }
  if (by.list) {
    const out = new Set<Capability>([def.capability])
    const flags = typeof value === 'string' ? value.split(',') : value === undefined || value === null ? [] : Object.keys(by.capabilities)
    for (const flag of flags) {
      const caps = options(flag)
      const pick = caps.find(c => declared.includes(c)) ?? (typeof value === 'string' ? caps[0] : undefined)
      if (pick) out.add(pick)
    }
    return [...out]
  }
  if (typeof value === 'string') return options(value).slice(0, 1)
  return [...new Set(Object.keys(by.capabilities).flatMap(options))]
}

/**
 * Whether a call's evaluated input needs only what the approved tool has: for a list input,
 * every flag that maps to capabilities needs one of them. The broker repeats this check.
 */
export function inputCapabilitiesMet (def: OperationDef, input: unknown, have: readonly string[]): boolean {
  const by = def.capabilityByInput
  if (!by) return true
  const value = input && typeof input === 'object' ? (input as Record<string, unknown>)[by.input] : undefined
  if (!by.list) {
    const needed = operationCapabilities(def, input)
    return needed.length > 0 && needed.every(c => have.includes(c))
  }
  if (typeof value !== 'string') return true
  return value.split(',').every(flag => {
    const caps = by.capabilities[flag]
    if (caps === undefined) return true
    return (Array.isArray(caps) ? caps : [caps]).some(c => have.includes(c))
  })
}

export interface InputIssue { path: string, message: string }

function checkField (field: OperationField, value: unknown, path: string, out: InputIssue[]): void {
  switch (field.type) {
    case 'integer':
      if (typeof value !== 'number' || !Number.isInteger(value)) { out.push({ path, message: 'must be a whole number' }); return }
      if (field.min !== undefined && value < field.min) out.push({ path, message: `must be at least ${field.min}` })
      if (field.max !== undefined && value > field.max) out.push({ path, message: `must be at most ${field.max}` })
      if (field.values && !field.values.includes(value)) out.push({ path, message: `must be one of ${field.values.join(', ')}` })
      return
    case 'number':
      if (typeof value !== 'number' || !Number.isFinite(value)) { out.push({ path, message: 'must be a number' }); return }
      if (field.min !== undefined && value < field.min) out.push({ path, message: `must be at least ${field.min}` })
      if (field.max !== undefined && value > field.max) out.push({ path, message: `must be at most ${field.max}` })
      return
    case 'string':
      if (typeof value !== 'string') { out.push({ path, message: 'must be text' }); return }
      if (field.max !== undefined && [...value].length > field.max) out.push({ path, message: `must be at most ${field.max} characters` })
      if (field.pattern && !new RegExp(field.pattern).test(value)) out.push({ path, message: 'has an invalid format' })
      if (field.values && !field.lenient && !field.values.includes(value)) out.push({ path, message: `must be one of ${field.values.join(', ')}` })
      return
    case 'boolean':
      if (typeof value !== 'boolean') out.push({ path, message: 'must be true or false' })
      return
    case 'ids':
      if (!Array.isArray(value) || value.some(v => typeof v !== 'number' || !Number.isInteger(v) || v < 1)) { out.push({ path, message: 'must be a list of ids' }); return }
      if (field.required && !value.length) out.push({ path, message: 'must not be empty' })
      if (field.maxItems !== undefined && value.length > field.maxItems) out.push({ path, message: `must have at most ${field.maxItems} items` })
      return
    case 'list':
      if (!Array.isArray(value)) { out.push({ path, message: 'must be a list' }); return }
      if (field.required && !value.length) out.push({ path, message: 'must not be empty' })
      if (field.maxItems !== undefined && value.length > field.maxItems) { out.push({ path, message: `must have at most ${field.maxItems} items` }); return }
      value.forEach((item, i) => checkObject(field.item ?? {}, item, `${path}[${i}]`, out))
      return
    case 'object':
      if (!value || typeof value !== 'object' || Array.isArray(value)) out.push({ path, message: 'must be an object' })
  }
}

function isAbsent (field: OperationField, value: unknown): boolean {
  if (value === undefined || value === null) return true
  return field.type === 'string' && value === '' && !field.required
}

function checkObject (fields: Record<string, OperationField>, value: unknown, path: string, out: InputIssue[]): void {
  if (!value || typeof value !== 'object' || Array.isArray(value)) { out.push({ path: path || '$', message: 'must be an object' }); return }
  const obj = value as Record<string, unknown>
  for (const key of Object.keys(obj)) {
    if (!fields[key]) out.push({ path: path ? `${path}.${key}` : key, message: 'is not an input of this operation' })
  }
  for (const [key, field] of Object.entries(fields)) {
    const sub = path ? `${path}.${key}` : key
    if (isAbsent(field, obj[key])) {
      if (field.required) out.push({ path: sub, message: 'is required' })
      continue
    }
    checkField(field, obj[key], sub, out)
  }
}

/** Checks evaluated input before the broker sends it; the backend repeats the same checks. */
export function checkOperationInput (operation: string, input: unknown): InputIssue[] {
  const def = OPERATIONS[operation]
  if (!def) return [{ path: '$', message: `unknown operation ${operation}` }]
  const out: InputIssue[] = []
  checkObject(def.input, input, '', out)
  return out
}
